/**
 * useInfiniteConversationHistory - Infinite Scroll do Histórico agrupado
 *
 * Consome GET /conversations/history: uma linha por par (contato, canal),
 * só conversas fechadas, ordenada pela mais recente (lastMessageAt desc).
 *
 * queryKey prefixada com 'conversations' de propósito: useCloseConversation /
 * useUpdateConversation já invalidam ['conversations'] — fechar uma conversa
 * atualiza a aba Histórico automaticamente, sem plumbing extra de socket.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { chatService } from '../services';

export interface UseInfiniteConversationHistoryOptions {
  /** Termo de busca (nome/telefone/email do contato — resolvido server-side) */
  search?: string;
  /** Limite de itens por página */
  limit?: number;
  /** Habilitar/desabilitar query (condicionar à aba ativa) */
  enabled?: boolean;
}

export const useInfiniteConversationHistory = (options: UseInfiniteConversationHistoryOptions = {}) => {
  const { search, limit = 25, enabled = true } = options;

  return useInfiniteQuery({
    queryKey: ['conversations', 'history', 'infinite', { search: search || '' }],
    queryFn: async ({ pageParam, signal }) => {
      return chatService.getConversationHistoryGroups(
        {
          page: pageParam as number,
          limit,
          ...(search?.trim() ? { search: search.trim() } : {}),
        },
        signal
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
};
